import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { resolveFileUrl } from '../../lib/portalHelpers';
import type { Certificate, PortalClass } from '../../types/portal';
import { Award, Download, BookOpen, Calendar, AlertCircle } from 'lucide-react';

const StudentCertificatesPage: React.FC = () => {
  const { user } = useStudentAuth();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const openCertificate = async (c: Certificate) => {
    setDownloadError(null);
    const target = await resolveFileUrl('certificates', c.storage_path, c.file_url);
    if (target) {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else {
      setDownloadError('Das Zertifikat konnte nicht geöffnet werden. Bitte versuche es erneut oder kontaktiere uns.');
    }
  };

  const load = useCallback(async () => {
    if (!user) return;
    setLoadError(null);
    setLoading(true);
    try {
      const [{ data: certData, error: certErr }, { data: cData, error: cErr }] = await Promise.all([
        supabase.from('certificates').select('*').eq('student_id', user.id).order('issued_at', { ascending: false }),
        supabase.from('portal_classes').select('*'),
      ]);
      if (certErr) throw certErr;
      if (cErr) throw cErr;
      setCertificates((certData ?? []) as Certificate[]);
      setClasses((cData ?? []) as PortalClass[]);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setLoadError('Zertifikate konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const classMap = Object.fromEntries(classes.map(c => [c.id, c.title]));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 dark:text-red-400 mb-1">
            Erfolge
          </p>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Meine Zertifikate</h1>
        </div>
        {!loading && certificates.length > 0 && (
          <p className="text-sm text-gray-400 dark:text-neutral-500 pb-0.5">
            {certificates.length} {certificates.length === 1 ? 'Zertifikat' : 'Zertifikate'}
          </p>
        )}
      </div>

      {downloadError && (
        <div className="flex items-start gap-3 p-4 bg-[#970000]/5 dark:bg-red-500/10 border border-[#970000]/20 dark:border-red-500/40 rounded-2xl">
          <AlertCircle className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
          <p className="flex-1 text-sm text-gray-900 dark:text-neutral-100">{downloadError}</p>
          <button
            onClick={() => setDownloadError(null)}
            className="text-xs text-gray-400 dark:text-neutral-500 font-semibold hover:text-gray-600 dark:hover:text-neutral-300 shrink-0"
          >
            Schließen
          </button>
        </div>
      )}

      {/* Content */}
      {loadError ? (
        <div className="flex items-start gap-3 p-5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-2xl">
          <AlertCircle className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-neutral-100">{loadError}</p>
          </div>
          <button onClick={load} className="text-xs text-[#970000] dark:text-red-400 font-semibold underline hover:no-underline shrink-0">
            Erneut versuchen
          </button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 h-44" />
          ))}
        </div>
      ) : certificates.length === 0 ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-4">
            <Award className="h-7 w-7 text-gray-300 dark:text-neutral-600" />
          </div>
          <p className="font-semibold text-gray-900 dark:text-neutral-100 mb-1">Noch keine Zertifikate</p>
          <p className="text-sm text-gray-400 dark:text-neutral-500">
            Sobald du einen Kurs abschließt, findest du dein Zertifikat hier.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {certificates.map(c => (
            <div
              key={c.id}
              className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden flex flex-col"
            >
              {/* Gold accent bar */}
              <div className="h-0.5 bg-gradient-to-r from-[#c8a030] to-[#e0c060]" />

              <div className="p-5 flex-1 flex flex-col">
                {/* Icon + title */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="shrink-0 w-11 h-11 rounded-2xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center">
                    <Award className="h-5 w-5 text-[#970000] dark:text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="font-bold text-gray-900 dark:text-neutral-100 leading-snug line-clamp-2">{c.title}</p>
                    {c.class_id && classMap[c.class_id] && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <BookOpen className="h-3 w-3 text-gray-300 dark:text-neutral-600" />
                        <p className="text-xs text-gray-400 dark:text-neutral-500">{classMap[c.class_id]}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1.5 mb-4">
                  <Calendar className="h-3.5 w-3.5 text-gray-300 dark:text-neutral-600" />
                  <p className="text-xs text-gray-400 dark:text-neutral-500">
                    Ausgestellt am{' '}
                    <span className="font-semibold text-gray-500 dark:text-neutral-400">
                      {new Date(c.issued_at).toLocaleDateString('de-DE', {
                        day: '2-digit', month: 'long', year: 'numeric',
                      })}
                    </span>
                  </p>
                </div>

                {/* Download button */}
                <div className="mt-auto">
                  <button
                    type="button"
                    onClick={() => openCertificate(c)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#970000] text-white text-sm font-semibold rounded-xl hover:bg-[#7a0000] transition-colors shadow-[0_2px_8px_rgba(151,0,0,0.2)]"
                  >
                    <Download className="h-4 w-4" />
                    Zertifikat herunterladen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentCertificatesPage;
